/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

let collectionRequestController = null
let collectionRequestId = 0
let collectionRateLimitedUntil = 0

const buildCollectionUrl = (params) => {
  const queryString = params.toString()
  return queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname
}

const loadCollection = async () => {
  const now = Date.now()

  if (now < collectionRateLimitedUntil) {
    console.warn('Collection loading paused due to rate limit')
    return
  }

  const requestId = ++collectionRequestId

  if (collectionRequestController) {
    collectionRequestController.abort()
  }

  collectionRequestController = new AbortController()

  const collectionProducts = document.querySelector('.collection-products')
  const productList = collectionProducts?.querySelector('.product-list')
  const filters = document.querySelector('.collection-filters')

  if (productList) {
    productList.style.opacity = '.2'
  }

  try {
    const response = await fetch(window.location.href, {
      signal: collectionRequestController.signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })

    if (response.status === 429) {
      const retryAfter = Number(response.headers.get('Retry-After')) || 10
      collectionRateLimitedUntil = Date.now() + retryAfter * 1000
      console.warn(`Rate limited. Retry after ${retryAfter}s`)
      return
    }

    if (!response.ok) {
      throw new Error(`Collection request failed: ${response.status}`)
    }

    const data = await response.text()

    if (requestId !== collectionRequestId) return

    const parser = new DOMParser()
    const newDocument = parser.parseFromString(data, 'text/html')

    const newCollectionProducts = newDocument.querySelector('.collection-products')
    const newFilters = newDocument.querySelector('.collection-filters')

    if (!newCollectionProducts) {
      throw new Error('Missing .collection-products in response')
    }

    collectionProducts?.replaceWith(newCollectionProducts)

    if (filters && newFilters) {
      filters.replaceWith(newFilters)
    }

    window.dispatchEvent(new CustomEvent('kt.collection.loaded'))

    const updatedCollectionProducts = document.querySelector('.collection-products')
    const navbarHeight = document.querySelector('[id*="__navbar"].sticky-top')?.clientHeight || 0

    if (updatedCollectionProducts) {
      updatedCollectionProducts.style.scrollMarginTop = `${navbarHeight}px`
      updatedCollectionProducts.scrollIntoView()
    }
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error loading collection:', error)
    }
  } finally {
    if (requestId === collectionRequestId) {
      collectionRequestController = null
    }

    const currentProductList = document.querySelector('.collection-products .product-list')
    if (currentProductList) {
      currentProductList.style.opacity = ''
    }
  }
}

class CollectionFilters extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')

    if (!this.form) return

    this.form.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const params = new URLSearchParams(new FormData(this.form))

        params.delete('page')

        const url = buildCollectionUrl(params)
        window.history.replaceState({}, '', url)

        loadCollection()
      })
    })

    this.querySelectorAll('.btn-filters-clear-all').forEach(btn => {
      const params = new URLSearchParams(window.location.search)

      for (const key of params.keys()) {
        if (key.includes('filter.')) {
          btn.removeAttribute('hidden')
        }
      }

      btn.addEventListener('click', () => {
        const params = new URLSearchParams(window.location.search)

        for (const key of [...params.keys()]) {
          if (key.includes('filter.')) {
            params.delete(key)
          }
        }

        params.delete('page')

        const url = buildCollectionUrl(params)
        window.history.replaceState({}, '', url)

        loadCollection()
      })
    })
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters)
}

class SortBy extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        this.setUrl(input.value)
        loadCollection()
      })
    })
  }

  setUrl (value) {
    if (!value) return

    const params = new URLSearchParams(window.location.search)

    params.set('sort_by', value)
    params.delete('page')

    const url = buildCollectionUrl(params)
    window.history.replaceState({}, '', url)
  }
}

if (!customElements.get('sort-by')) {
  customElements.define('sort-by', SortBy)
}

class Pagination extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('a').forEach(elem => {
      elem.addEventListener('click', (event) => {
        event.preventDefault()

        this.setUrl(elem.dataset.newPage)
        loadCollection()
      })
    })
  }

  setUrl (value) {
    if (!value) return

    const params = new URLSearchParams(window.location.search)

    params.set('page', value)

    const url = buildCollectionUrl(params)
    window.history.replaceState({}, '', url)
  }
}

if (!customElements.get('collection-pagination')) {
  customElements.define('collection-pagination', Pagination)
}